import React, { useEffect, useState } from 'react'
import { ExportProgramModalProps } from './ExternalProgramModal.props'
import Modal from 'components/Modal'
import {
  ExternalModalProgramContainer,
  ExternalModalScheduleContainer,
  ExternalModalScheduleItem,
} from './ExternalProgramModal.styled'
import { Text } from 'components/Text'
import { useAppDispatch, useAppState } from 'store'
import { programActions } from 'store/slices/programs'
import { userActions } from 'store/slices/users/users.slice'
import moment from 'moment'
import Button from 'components/Button/Button'
import Divider from 'components/Divider'
import RadioButton from 'components/RadioButton/RadioButton'
import {
  TransactionPaymentContainer,
  TransactionModalContentContainer,
  TransactionAmountItem,
  TransactionPaymentItem,
} from 'components/TransactionModal/TransactionModal.styled'

import { PENNIES } from 'utils/rate'
import { Input } from 'components/Input'
import Select from 'components/Select'
import { SelectTypes } from 'components/Select/Select.props'
import { PaymentMethod } from 'models/payment.model'
import cogoToast from 'cogo-toast'
import Spinner from 'components/Spinner'
import { SpinnerContainer } from 'components/Spinner/Spinner.styled'

const ExternalProgramModal: React.FC<ExportProgramModalProps> = ({
  programId,
  locationId,
  onClose,
}) => {
  const MYSELF = 'Myself'
  const dispatch = useAppDispatch()
  const { currentProgram, programSchedule, currentProgramStatus } = useAppState(
    state => state.programs
  )
  const { userProfile } = useAppState(state => state.user)
  const { user } = useAppState(state => state.auth)
  const [paymentMethod, setPaymentMethod] = useState<SelectTypes[]>([])
  const [
    paymentMethodSelected,
    setPaymentMethodSelected,
  ] = useState<SelectTypes | null>(null)

  const capitalizeFirstLetter = (string: string): string => {
    return string.charAt(0).toUpperCase() + string.slice(1)
  }

  const handleClose = () => {
    dispatch(programActions.cleanCurrentProgram())
    onClose()
  }

  const handleSubmit = () => {
    if (user) {
      dispatch(
        programActions.createProgramRegistration({
          locationId,
          programId,
          userId: user?.id.toString(),
        })
      )
        .then((data: any) => {
          if (!data.payload.error) {
            cogoToast.success('Program registered successfully', {
              position: 'top-right',
            })
            onClose()
          }
        })
        .catch(error => console.error(error))
    }
  }

  useEffect(() => {
    if (!currentProgram) {
      dispatch(programActions.getProgramById({ locationId, programId }))
    }
    if (!programSchedule) {
      dispatch(programActions.getProgramScheduleById({ locationId, programId }))
    }
    if (!userProfile && user) {
      dispatch(userActions.getUserProfile(427))
    }
  }, [currentProgram, programId])

  useEffect(() => {
    if (userProfile && userProfile.payment_methods.length > 0) {
      const { payment_methods } = userProfile
      const methods: SelectTypes[] = payment_methods.map(
        (method: PaymentMethod) => {
          return {
            label: `**** **** **** ${method.card_number}`,
            value: method.id.toString(),
          }
        }
      )
      setPaymentMethod(methods)
    }
  }, [userProfile])

  return (
    <Modal onClose={handleClose} title={currentProgram?.name ?? ''}>
      {currentProgramStatus === 'loading' ? (
        <SpinnerContainer style={{ minHeight: 530 }}>
          <Spinner loading={true} />
        </SpinnerContainer>
      ) : (
        currentProgram && (
          <ExternalModalProgramContainer>
            <Text style={{ marginTop: 20 }}>
              {capitalizeFirstLetter(currentProgram.program_capacity)}
            </Text>
            {programSchedule && programSchedule.length > 0 && (
              <>
                <Text>Schedule</Text>
                <ExternalModalScheduleContainer>
                  {programSchedule?.length &&
                    programSchedule.map(schedule => (
                      <ExternalModalScheduleItem key={schedule.id}>
                        <Button
                          text={moment(schedule.date).format('MMMM DD, YYYY')}
                          variant="primary"
                          preset="ghost"
                          size={'small'}
                        />
                      </ExternalModalScheduleItem>
                    ))}
                </ExternalModalScheduleContainer>
              </>
            )}
            <Divider />
            <TransactionPaymentContainer>
              <TransactionModalContentContainer>
                <TransactionAmountItem>
                  <Input label="Player" required value={MYSELF} disabled />
                </TransactionAmountItem>
                <TransactionAmountItem>
                  <Input
                    label="Price"
                    currencyMask
                    value={(currentProgram.price / PENNIES).toFixed(2)}
                    disabled
                  />
                </TransactionAmountItem>
              </TransactionModalContentContainer>
              <TransactionModalContentContainer>
                <Text
                  text="Payment Method"
                  weight="600"
                  style={{ marginBottom: 10 }}
                />

                <TransactionPaymentItem>
                  <RadioButton
                    key="radio-payment-1"
                    weight="500"
                    name="payment"
                    text={['Card on file']}
                    value="card_on_file"
                  />
                </TransactionPaymentItem>
                <TransactionPaymentItem>
                  <Select
                    autocomplete
                    options={paymentMethod}
                    value={paymentMethodSelected}
                    onChange={e => setPaymentMethodSelected(e as SelectTypes)}
                  />
                </TransactionPaymentItem>

                {paymentMethod && paymentMethod.length > 0 && (
                  <Button
                    text="Register"
                    preset="filled"
                    variant="secondary"
                    size="small"
                    disabled={!paymentMethodSelected}
                    onClick={handleSubmit}
                    style={{ width: '40%', margin: '20px 0' }}
                  />
                )}
              </TransactionModalContentContainer>
            </TransactionPaymentContainer>
          </ExternalModalProgramContainer>
        )
      )}
    </Modal>
  )
}

export default ExternalProgramModal
